import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { CANCEL_ABSENCE } from '@/modules/API/slot/regularUpdate'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Absence } from '@/types/Slots'
import { Daycare } from '@/types/Daycare'
import styles from './cancelAbsenceRegularSlot.module.scss'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { Close, DoDisturbOff } from '@mui/icons-material'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface CancelAbsenceRegularSlotProps {
    open: boolean
    handleClose: () => void
    refetchData: () => void
    closeParent: () => void
    absences: Absence[]
    daycare: Daycare
}

const CancelAbsenceRegularSlot: FunctionComponent<CancelAbsenceRegularSlotProps> = ({
    open,
    handleClose,
    refetchData,
    closeParent,
    absences,
    daycare
}: CancelAbsenceRegularSlotProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [selectChilds, setSelectChilds] = useState<string[]>(
        absences.map((abs: Absence): string => abs.regularSlotId.toString())
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: absences.map((abs: Absence): SelectOptionDeprecated => {
                return {
                    name: abs.childName + (abs.childComplement ? ' - ' + abs.childComplement : ''),
                    value: abs.regularSlotId.toString()
                }
            })
        }
    ]

    const [cancelAbsence, { loading: loadingCancel }] = useMutation(CANCEL_ABSENCE, {
        onCompleted: () => {
            refetchData()
            closeParent()
            handleClose()

            showNotificationSuccess(t('absenceCanceled'))
        },
        onError: () => {
            showNotificationError(t('errorCanceledAbsence'))
        }
    })

    function cancelAllAbsences(): void {
        absences
            .filter((abs: Absence): boolean => selectChilds.includes(abs.regularSlotId.toString()))
            .forEach((abs: Absence): void => {
                cancelAbsence({
                    variables: {
                        regular_slot_id: abs.regularSlotId,
                        date: abs.date,
                        daycare_id: daycare.id
                    }
                })
            })
    }

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('cancelAbsence')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>{t('cancelAbsenceMessage')}</p>
                {absences.length > 1 && (
                    <SelectMultiple
                        selectOptions={selectOptionsChild}
                        selectValues={selectChilds}
                        setSelectValues={setSelectChilds}
                        className={styles.UserInput}
                        label={t('childs')}
                    />
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('yes')}
                        customIcon={<DoDisturbOff />}
                        onClick={cancelAllAbsences}
                        className={styles.Button}
                        disabled={loadingCancel}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('no')}
                        customIcon={<Close />}
                        onClick={handleClose}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default CancelAbsenceRegularSlot
